import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Calendar as CalendarIcon, X, Loader2, Upload, ImageIcon, MapPin, Users, Mail, Phone, Building2, Tag, Plus, Trash2, DollarSign, Gift, FileText, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface SponsorshipPackage {
  id: string;
  name: string;
  price: string;
  benefits: string;
}

const CATEGORIES = [
  { value: "corporate", labelEn: "Corporate Events", labelAr: "فعاليات الشركات" },
  { value: "entertainment", labelEn: "Entertainment", labelAr: "ترفيه" },
  { value: "sports", labelEn: "Sports", labelAr: "رياضة" },
  { value: "technology", labelEn: "Technology", labelAr: "تقنية" },
  { value: "music", labelEn: "Music & Concerts", labelAr: "موسيقى وحفلات" },
  { value: "arts", labelEn: "Arts & Culture", labelAr: "فنون وثقافة" },
  { value: "education", labelEn: "Education & Conferences", labelAr: "تعليم ومؤتمرات" },
  { value: "charity", labelEn: "Charity & Non-profit", labelAr: "خيري" },
  { value: "food", labelEn: "Food & Beverage", labelAr: "طعام ومشروبات" },
  { value: "fashion", labelEn: "Fashion", labelAr: "موضة" },
];

const EVENT_TYPES = [
  { value: "conference", labelEn: "Conference", labelAr: "مؤتمر" },
  { value: "exhibition", labelEn: "Exhibition", labelAr: "معرض" },
  { value: "festival", labelEn: "Festival", labelAr: "مهرجان" },
  { value: "workshop", labelEn: "Workshop", labelAr: "ورشة عمل" },
  { value: "gala", labelEn: "Gala & Awards", labelAr: "حفل جوائز" },
  { value: "sports_event", labelEn: "Sports Event", labelAr: "فعالية رياضية" },
  { value: "product_launch", labelEn: "Product Launch", labelAr: "إطلاق منتج" },
  { value: "networking", labelEn: "Networking Event", labelAr: "فعالية تواصل" },
];

const EventListingSignUp = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isRTL = language === 'ar';

  const [loading, setLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingPermit, setUploadingPermit] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [eventDate, setEventDate] = useState<Date>();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [packages, setPackages] = useState<SponsorshipPackage[]>([]);
  const [permitFile, setPermitFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    organizer: "",
    category: "",
    description: "",
    location: "",
    expectedAttendees: "",
    email: user?.email || "",
    phone: "",
    image: "",
    eventPermitUrl: "",
  });

  const handleTypeToggle = (value: string) => {
    setSelectedTypes(prev => 
      prev.includes(value) 
        ? prev.filter(t => t !== value) 
        : [...prev, value]
    );
  };

  const addPackage = () => {
    setPackages(prev => [...prev, {
      id: crypto.randomUUID(),
      name: "",
      price: "",
      benefits: ""
    }]);
  };

  const updatePackage = (id: string, field: keyof SponsorshipPackage, value: string) => {
    setPackages(prev => prev.map(pkg => 
      pkg.id === id ? { ...pkg, [field]: value } : pkg
    ));
  };

  const removePackage = (id: string) => {
    setPackages(prev => prev.filter(pkg => pkg.id !== id));
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى اختيار ملف صورة" : "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "حجم الصورة يجب أن يكون أقل من 5 ميجابايت" : "Image size must be less than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingImage(true);

    try {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);

      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/event-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('influencer-images')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('influencer-images')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, image: publicUrl }));
      toast({
        title: isRTL ? "تم الرفع" : "Uploaded",
        description: isRTL ? "تم رفع الصورة بنجاح" : "Image uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading image:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفع الصورة" : "Failed to upload image",
        variant: "destructive",
      });
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const handlePermitUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;

    // Validate file type (PDF, images)
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى اختيار ملف PDF أو صورة" : "Please select a PDF or image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "حجم الملف يجب أن يكون أقل من 10 ميجابايت" : "File size must be less than 10MB",
        variant: "destructive",
      });
      return;
    }

    setUploadingPermit(true);
    setPermitFile(file);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/event-permit-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('license-documents')
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('license-documents')
        .getPublicUrl(fileName);

      setFormData(prev => ({ ...prev, eventPermitUrl: publicUrl }));

      toast({
        title: isRTL ? "تم الرفع" : "Uploaded",
        description: isRTL ? "تم رفع تصريح الفعالية بنجاح" : "Event permit uploaded successfully",
      });
    } catch (error: any) {
      console.error('Error uploading permit:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "فشل في رفع التصريح" : "Failed to upload permit",
        variant: "destructive",
      });
      setPermitFile(null);
    } finally {
      setUploadingPermit(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يجب تسجيل الدخول أولاً" : "You must be logged in first",
        variant: "destructive",
      });
      navigate('/auth');
      return;
    }

    if (!formData.name || !formData.category) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى ملء جميع الحقول المطلوبة" : "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate event permit
    if (!formData.eventPermitUrl) {
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: isRTL ? "يرجى رفع تصريح الفعالية" : "Please upload the event permit",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // Format packages for storage
      const formattedPackages = packages
        .filter(pkg => pkg.name && pkg.price)
        .map(pkg => ({
          name: pkg.name,
          price: parseFloat(pkg.price) || 0,
          benefits: pkg.benefits,
        }));

      const eventData = {
        user_id: user.id,
        name: formData.name,
        company_name: formData.organizer || null,
        category: formData.category,
        description: formData.description || null,
        event_history: JSON.stringify({
          location: formData.location || null,
          expected_attendees: formData.expectedAttendees || null,
          event_date: eventDate ? eventDate.toISOString() : null,
          event_types: selectedTypes,
        }),
        sponsorship_packages: formattedPackages,
        portfolio_urls: [],
        social_links: {},
        contact_info: {
          email: formData.email || user.email,
          phone: formData.phone || null,
        },
        image: formData.image || null,
        event_permit_url: formData.eventPermitUrl,
        approval_status: 'pending',
        featured: false,
        rating: 4.5,
        total_events: 0,
      };

      const { error } = await supabase
        .from('event_organizers')
        .insert(eventData);

      if (error) throw error;

      toast({
        title: isRTL ? "تم إضافة الفعالية بنجاح!" : "Event Listed Successfully!",
        description: isRTL 
          ? "سيتم مراجعة فعاليتك وستظهر قريباً للرعاة" 
          : "Your event is under review. It will be visible to sponsors soon.",
      });

      navigate('/events-sponsors');
    } catch (error: any) {
      console.error('Error submitting event listing:', error);
      toast({
        title: isRTL ? "خطأ" : "Error",
        description: error.message || (isRTL ? "حدث خطأ أثناء التقديم" : "An error occurred while submitting"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="mb-4 text-lg">{isRTL ? "يجب تسجيل الدخول لإضافة فعالية" : "You must be logged in to list an event"}</p>
            <Button onClick={() => navigate('/auth')} size="lg">
              {isRTL ? "تسجيل الدخول" : "Login"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-gradient-to-b from-background to-muted/20 ${isRTL ? 'text-right' : 'text-left'}`}>
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/events-sponsors')}
            className="mb-4"
          >
            <ArrowLeft className={`h-4 w-4 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} />
            {isRTL ? "العودة" : "Back"}
          </Button>
          
          <div className="text-center">
            <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-full mb-4">
              <CalendarIcon className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              {isRTL ? "أضف فعاليتك" : "List Your Event"}
            </h1>
            <p className="text-muted-foreground max-w-md mx-auto">
              {isRTL 
                ? "أضف تفاصيل فعاليتك للعثور على الرعاة المناسبين"
                : "Add your event details to connect with potential sponsors"}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Event Image Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <ImageIcon className="h-5 w-5" />
                {isRTL ? "صورة الفعالية" : "Event Image"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="relative w-40 h-40 rounded-xl border-2 border-dashed border-muted-foreground/25 overflow-hidden bg-muted/50 flex items-center justify-center">
                  {imagePreview || formData.image ? (
                    <img 
                      src={imagePreview || formData.image} 
                      alt="Event" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <ImageIcon className="h-12 w-12 text-muted-foreground/50" />
                  )}
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <label htmlFor="image-upload" className="cursor-pointer inline-block">
                    <div className="inline-flex items-center gap-2 px-6 py-3 bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors">
                      {uploadingImage ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin" />
                          <span>{isRTL ? "جاري الرفع..." : "Uploading..."}</span>
                        </>
                      ) : (
                        <>
                          <Upload className="h-5 w-5" />
                          <span>{isRTL ? "اختر صورة" : "Upload Image"}</span>
                        </>
                      )}
                    </div>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </label>
                  <p className="text-sm text-muted-foreground mt-2">
                    {isRTL ? "PNG, JPG حتى 5 ميجابايت" : "PNG, JPG up to 5MB"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Basic Info Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Tag className="h-5 w-5" />
                {isRTL ? "معلومات الفعالية" : "Event Information"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name">{isRTL ? "اسم الفعالية *" : "Event Name *"}</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder={isRTL ? "مثال: مؤتمر التقنية 2025" : "e.g., Tech Conference 2025"}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="organizer">{isRTL ? "المنظم / الشركة" : "Organizer / Company"}</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="organizer"
                      value={formData.organizer}
                      onChange={(e) => setFormData(prev => ({ ...prev, organizer: e.target.value }))}
                      placeholder={isRTL ? "اسم الشركة المنظمة" : "Organizing company name"}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>{isRTL ? "فئة الفعالية *" : "Event Category *"}</Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isRTL ? "اختر الفئة" : "Select category"} />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {isRTL ? cat.labelAr : cat.labelEn}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">{isRTL ? "وصف الفعالية" : "Event Description"}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder={isRTL ? "صف فعاليتك وما يميزها للرعاة المحتملين..." : "Describe your event and what makes it unique for potential sponsors..."}
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>

          {/* Event Details Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <CalendarIcon className="h-5 w-5" />
                {isRTL ? "تفاصيل الفعالية" : "Event Details"}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-2">
                  <Label>{isRTL ? "تاريخ الفعالية" : "Event Date"}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !eventDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {eventDate ? format(eventDate, "PPP") : (isRTL ? "اختر التاريخ" : "Select date")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 z-50" align="start">
                      <Calendar
                        mode="single"
                        selected={eventDate}
                        onSelect={setEventDate}
                        initialFocus
                        className="p-3 pointer-events-auto"
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location">{isRTL ? "الموقع" : "Location"}</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                      placeholder={isRTL ? "المدينة، المكان" : "City, Venue"}
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="attendees">{isRTL ? "الحضور المتوقع" : "Expected Attendees"}</Label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="attendees"
                      value={formData.expectedAttendees}
                      onChange={(e) => setFormData(prev => ({ ...prev, expectedAttendees: e.target.value }))}
                      placeholder={isRTL ? "مثال: 500" : "e.g., 500"}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <Label>{isRTL ? "نوع الفعالية" : "Event Type"}</Label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_TYPES.map(type => (
                    <Badge
                      key={type.value}
                      variant={selectedTypes.includes(type.value) ? "default" : "outline"}
                      className="cursor-pointer transition-all hover:scale-105"
                      onClick={() => handleTypeToggle(type.value)}
                    >
                      {isRTL ? type.labelAr : type.labelEn}
                      {selectedTypes.includes(type.value) && (
                        <X className="h-3 w-3 ml-1" />
                      )}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sponsorship Packages Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Gift className="h-5 w-5" />
                {isRTL ? "باقات الرعاية" : "Sponsorship Packages"}
              </CardTitle>
              <CardDescription>
                {isRTL ? "حدد باقات الرعاية المتاحة للرعاة المحتملين" : "Define sponsorship tiers available for potential sponsors"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {packages.length === 0 ? (
                <div className="text-center py-8 border-2 border-dashed rounded-lg">
                  <Gift className="h-10 w-10 mx-auto mb-3 text-muted-foreground/50" />
                  <p className="text-muted-foreground mb-4">
                    {isRTL ? "لم تتم إضافة باقات بعد" : "No packages added yet"}
                  </p>
                  <Button type="button" variant="outline" onClick={addPackage}>
                    <Plus className="h-4 w-4 mr-2" />
                    {isRTL ? "إضافة باقة رعاية" : "Add Sponsorship Package"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {packages.map((pkg, index) => (
                    <Card key={pkg.id} className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <span className="text-sm font-medium text-muted-foreground">
                          {isRTL ? `باقة ${index + 1}` : `Package ${index + 1}`}
                        </span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removePackage(pkg.id)}
                          className="text-destructive hover:text-destructive h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>{isRTL ? "اسم الباقة" : "Package Name"}</Label>
                          <Input
                            value={pkg.name}
                            onChange={(e) => updatePackage(pkg.id, 'name', e.target.value)}
                            placeholder={isRTL ? "مثال: باقة ذهبية" : "e.g., Gold Tier"}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{isRTL ? "السعر (ريال)" : "Price (SAR)"}</Label>
                          <div className="relative">
                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              type="number"
                              value={pkg.price}
                              onChange={(e) => updatePackage(pkg.id, 'price', e.target.value)}
                              placeholder="50000"
                              className="pl-10"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2 mt-4">
                        <Label>{isRTL ? "المميزات والفوائد" : "Benefits & Perks"}</Label>
                        <Textarea
                          value={pkg.benefits}
                          onChange={(e) => updatePackage(pkg.id, 'benefits', e.target.value)}
                          placeholder={isRTL 
                            ? "مثال: شعار على المسرح الرئيسي، منطقة عرض حصرية، 10 تذاكر VIP..."
                            : "e.g., Logo on main stage, exclusive booth area, 10 VIP tickets..."}
                          rows={2}
                        />
                      </div>
                    </Card>
                  ))}
                  <Button type="button" variant="outline" onClick={addPackage} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    {isRTL ? "إضافة باقة أخرى" : "Add Another Package"}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="h-5 w-5" />
                {isRTL ? "معلومات الاتصال" : "Contact Information"}
              </CardTitle>
              <CardDescription>
                {isRTL ? "كيف يمكن للرعاة التواصل معك" : "How sponsors can reach you"}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="email">{isRTL ? "البريد الإلكتروني" : "Email"}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={user?.email || ""}
                      disabled
                      className="pl-10 bg-muted"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{isRTL ? "رقم الهاتف" : "Phone Number"}</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+966 5XX XXX XXXX"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Event Permit Card */}
          <Card>
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {isRTL ? "تصريح الفعالية *" : "Event Permit *"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm mb-4">
                <p className="text-blue-800 dark:text-blue-200">
                  {isRTL 
                    ? "يجب رفع تصريح أو رخصة الفعالية الصادرة من الجهات المختصة للتحقق من صحة الفعالية."
                    : "Please upload the event permit/license issued by the relevant authorities to verify your event."}
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>{isRTL ? "رفع التصريح *" : "Upload Permit *"}</Label>
                <label htmlFor="permit-upload" className="cursor-pointer block">
                  <div className={`flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed rounded-lg transition-colors ${
                    formData.eventPermitUrl 
                      ? 'border-green-500 bg-green-50 dark:bg-green-950/30' 
                      : 'border-muted-foreground/25 hover:border-primary/50'
                  }`}>
                    {uploadingPermit ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        <span>{isRTL ? "جاري الرفع..." : "Uploading..."}</span>
                      </>
                    ) : formData.eventPermitUrl ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600" />
                        <span className="text-green-600">{permitFile?.name || (isRTL ? "تم رفع التصريح" : "Permit uploaded")}</span>
                      </>
                    ) : (
                      <>
                        <Upload className="h-5 w-5 text-muted-foreground" />
                        <span>{isRTL ? "اختر ملف التصريح" : "Choose permit file"}</span>
                      </>
                    )}
                  </div>
                  <input
                    id="permit-upload"
                    type="file"
                    accept=".pdf,image/*"
                    onChange={handlePermitUpload}
                    className="hidden"
                    disabled={uploadingPermit}
                  />
                </label>
                <p className="text-xs text-muted-foreground">
                  {isRTL ? "PDF أو صورة، حتى 10 ميجابايت" : "PDF or image, up to 10MB"}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex flex-col items-center gap-4 pt-4">
            <Button type="submit" size="lg" className="w-full sm:w-auto min-w-[200px]" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {isRTL ? "جاري الإرسال..." : "Submitting..."}
                </>
              ) : (
                isRTL ? "إضافة الفعالية" : "List My Event"
              )}
            </Button>
            <p className="text-sm text-center text-muted-foreground max-w-md">
              {isRTL 
                ? "سيتم مراجعة فعاليتك من قبل فريقنا قبل ظهورها للرعاة المحتملين"
                : "Your event will be reviewed by our team before being visible to potential sponsors"}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventListingSignUp;
